import { useTheme } from '@emotion/react';
import { createContext, forwardRef, useContext } from 'react';
import { IconBadge } from '../../visual-elements/IconBadge/index.js';
import { EMPTY_STATE_DEFAULT_ICONS } from '../../EmptyState/constants.js';
import { ComposableEmptyStateWrapper, ComposableEmptyStateTextWrapper, ComposableEmptyStateActionsWrapper } from './styles.js';
import { jsx } from '@emotion/react/jsx-runtime';

const EmptyStateContext = createContext({
  status: 'informational'
});
const useEmptyStateContext = () => {
  const context = useContext(EmptyStateContext);
  if (!context) {
    throw new Error('ComposableEmptyState components must be used within a ComposableEmptyState.Root');
  }
  return context;
};
const Root = forwardRef(({
  status = 'informational',
  children,
  ...rest
}, ref) => {
  const contextValue = {
    status
  };
  return jsx(EmptyStateContext.Provider, {
    value: contextValue,
    children: jsx(ComposableEmptyStateWrapper, {
      ref: ref,
      ...rest,
      children: children
    })
  });
});
const Icon = forwardRef(({
  icon,
  size = 'xl',
  ...rest
}, ref) => {
  const {
    status
  } = useEmptyStateContext();
  const {
    colorScheme
  } = useTheme();
  return jsx("div", {
    ref: ref,
    ...rest,
    children: jsx(IconBadge, {
      icon: icon !== null && icon !== void 0 ? icon : EMPTY_STATE_DEFAULT_ICONS[status],
      size: size,
      backgroundColor: colorScheme.status.default.background[status],
      color: colorScheme.status.default.content[status]
    })
  });
});
const TextWrapper = forwardRef(({
  children,
  ...rest
}, ref) => {
  return jsx(ComposableEmptyStateTextWrapper, {
    ref: ref,
    ...rest,
    children: children
  });
});
const Label = forwardRef(({
  children,
  ...rest
}, ref) => {
  return jsx("div", {
    ref: ref,
    ...rest,
    children: children
  });
});
const Description = forwardRef(({
  children,
  ...rest
}, ref) => {
  return jsx("div", {
    ref: ref,
    ...rest,
    children: children
  });
});
const Actions = forwardRef(({
  children,
  ...rest
}, ref) => {
  return jsx(ComposableEmptyStateActionsWrapper, {
    ref: ref,
    ...rest,
    children: children
  });
});
Root.displayName = 'ComposableEmptyState.Root';
Icon.displayName = 'ComposableEmptyState.Icon';
TextWrapper.displayName = 'ComposableEmptyState.TextWrapper';
Label.displayName = 'ComposableEmptyState.Label';
Description.displayName = 'ComposableEmptyState.Description';
Actions.displayName = 'ComposableEmptyState.Actions';
const ComposableEmptyState = Object.assign(Root, {
  Icon,
  TextWrapper,
  Label,
  Description,
  Actions
});

export { ComposableEmptyState };
